// Client-side service for workflow storage
// Integrates with Vercel API endpoints following your existing security patterns
// NOW WITH AUTH0 SECURITY

import logger from '../utils/logger';
import { useAuth0 } from '@auth0/auth0-react';

// API configuration - environment-agnostic
const API_BASE_URL = '/api';

// Debug logging
console.log('🔧 WorkflowStorageService Debug:', {
    isDev: import.meta.env.DEV,
    mode: import.meta.env.MODE,
    apiBaseUrl: API_BASE_URL
});

// Auth0 token management
let getAccessTokenSilently: (() => Promise<string>) | null = null;

// Initialize Auth0 token getter (called from components)
export function initializeAuth0TokenGetter(tokenGetter: () => Promise<string>) {
    getAccessTokenSilently = tokenGetter;
    logger.log('🔐 Auth0 token getter initialized for secure API calls');
}

// Public method to get auth token (for use by other services like sessionEncryption)
export async function getAuthTokenForAPI(): Promise<string | null> {
    return getAuthToken();
}

// Get Auth0 token for API calls with timeout
async function getAuthToken(): Promise<string | null> {
    console.log('🔐 WORKFLOW-SERVICE: getAuthToken called', {
        hasTokenGetter: !!getAccessTokenSilently,
        timestamp: new Date().toISOString()
    });

    if (!getAccessTokenSilently) {
        logger.warn('⚠️ Auth0 token getter not initialized - API calls may fail');
        console.log('⚠️ WORKFLOW-SERVICE: No token getter available');
        return null;
    }

    try {
        console.log('🔐 WORKFLOW-SERVICE: Calling getAccessTokenSilently with 5s timeout...');

        // Add timeout to prevent hanging
        const token = await Promise.race([
            getAccessTokenSilently(),
            new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Token retrieval timeout after 5 seconds')), 5000)
            )
        ]);

        if (!token) {
            logger.warn('⚠️ Token retrieval returned null/undefined');
            console.log('⚠️ WORKFLOW-SERVICE: Token is null/undefined');
            return null;
        }

        logger.log('✅ Auth0 token retrieved successfully');
        console.log('🔐 WORKFLOW-SERVICE: Token retrieved', {
            tokenLength: token.length,
            hasToken: true,
            timestamp: new Date().toISOString()
        });
        return token;
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('❌ Failed to get Auth0 token:', errorMessage);
        console.log('❌ WORKFLOW-SERVICE: Token retrieval failed', {
            error: errorMessage,
            errorType: error instanceof Error ? error.constructor.name : typeof error,
            timestamp: new Date().toISOString()
        });
        return null;
    }
}

// REMOVED: EnhancedScheduleParameters interface - using simple scheduleParameters instead

// Types for workflow storage
export interface WorkflowData {
    id?: string;
    name: string;
    description: string;
    category: 'governance' | 'token' | 'defi' | 'utility';
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    estimatedTime: number;
    tags: string[];
    created?: number;
    lastUsed?: number;
    useCount?: number;
    // Sensitive data (Tier2)
    steps?: any[];
    functionData?: string;
    contractAddresses?: string[];
    saltValues?: string[];
    customInputs?: any;
    templateData?: any;
    // ENHANCEMENT: Schedule parameters for timelock operations
    scheduleParameters?: {
        target: string;
        value: string;
        data: string;
        predecessor: string;
        salt: string;
        delay: string;
        operationHash?: string;
        executionTime?: string;
        scheduledAt?: string;
        scheduledBy?: string;
    };
    // ENHANCEMENT: Link to related workflow completion data
    workflowCompletionId?: string;
    transactionHash?: string;
    blockNumber?: number;
    gasUsed?: string;
}

export interface TemplateData {
    id?: string;
    contract: string;
    contractAddress: string;
    functionName: string;
    functionData: string;
    inputs: string[];
    timestamp: number;
    description?: string;
    tags?: string[];
}

export interface SaltData {
    salt: string;
    metadata?: {
        generated?: number;
        algorithm?: string;
        length?: number;
        purpose?: string;
    };
}

export interface StorageResponse<T = any> {
    success: boolean;
    data?: T;
    id?: string;
    message?: string;
    error?: string;
}

class WorkflowStorageService {
    private apiUrl: string;

    constructor() {
        this.apiUrl = `${API_BASE_URL}/workflow-storage`;
    }

    // Generic API call method with Auth0 security
    private async apiCall<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<StorageResponse<T>> {
        try {
            // Get Auth0 token for secure API calls
            const authToken = await getAuthToken();

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...options.headers as Record<string, string>,
            };

            // Add Authorization header if token is available
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
                logger.log('🔐 Including Auth0 token in API request');
            } else {
                logger.warn('⚠️ No Auth0 token available - API request may be rejected');
            }

            const response = await fetch(endpoint, {
                headers,
                ...options,
            });

            // CRITICAL FIX: Check if response is JSON before parsing
            const contentType = response.headers.get('content-type');
            let data;

            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                // Non-JSON response - likely an error page or CORS issue
                const textResponse = await response.text();
                logger.error('🚫 Non-JSON response from API:', {
                    status: response.status,
                    statusText: response.statusText,
                    contentType,
                    responsePreview: textResponse.substring(0, 200)
                });
                throw new Error(`API returned non-JSON response (${response.status}): ${textResponse.substring(0, 100)}`);
            }

            if (!response.ok) {
                // Handle authentication errors specifically
                if (response.status === 401) {
                    logger.error('🚫 Authentication failed - user may need to log in again');
                    logger.error('🚫 This usually indicates an Auth0 token issue or expired session');
                    throw new Error('Authentication required. Your session may have expired. Please refresh the page and log in again.');
                } else if (response.status === 403) {
                    logger.error('🚫 Access forbidden - insufficient permissions');
                    logger.error('🚫 User may not have the required API scopes: read:workflows, write:workflows');
                    throw new Error('Access forbidden. Your account does not have the required permissions for this operation.');
                } else if (response.status === 400) {
                    logger.error('🚫 Bad request - invalid API parameters');
                    logger.error('🚫 Request details:', { endpoint, method: options.method || 'GET' });
                    throw new Error(data.error || 'Invalid request. Please check your input and try again.');
                } else if (response.status >= 500) {
                    logger.error('🚫 Server error - backend service unavailable');
                    logger.error('🚫 Server response:', data);
                    throw new Error(`Server error: ${data.error || data.message || 'Backend service temporarily unavailable'}`);
                }

                throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            return data;
        } catch (error) {
            // CRITICAL FIX: Better error logging with full error details
            if (error instanceof Error) {
                logger.error('Workflow storage API error:', {
                    message: error.message,
                    name: error.name,
                    stack: error.stack?.split('\n').slice(0, 3).join('\n')
                });
            } else {
                logger.error('Workflow storage API error (non-Error object):', {
                    type: typeof error,
                    value: String(error),
                    stringified: JSON.stringify(error)
                });
            }
            throw error;
        }
    }

    // Workflow operations
    async storeWorkflow(workflowData: WorkflowData, id?: string): Promise<StorageResponse> {
        logger.log('📦 Storing workflow:', workflowData.name, 'with ID:', id);

        return this.apiCall(`${this.apiUrl}?action=store&type=workflow`, {
            method: 'POST',
            body: JSON.stringify({ data: workflowData, id: id }),
        });
    }

    async retrieveWorkflow(id: string): Promise<StorageResponse<WorkflowData>> {
        logger.log('📥 Retrieving workflow:', id);

        // CROSS-SESSION FIX: Get current session ID for re-encryption
        try {
            const { sessionEncryption } = await import('../utils/sessionEncryption');
            const session = await sessionEncryption.getSessionKey();

            logger.log('🔐 CROSS-SESSION FIX: Including session ID in retrieval request');
            return this.apiCall(`${this.apiUrl}?action=retrieve&type=workflow&id=${id}&sessionId=${session.sessionId}`);
        } catch (sessionError) {
            logger.warn('⚠️ CROSS-SESSION FIX: Session not available, retrieving without session ID');
            return this.apiCall(`${this.apiUrl}?action=retrieve&type=workflow&id=${id}`);
        }
    }

    async listWorkflows(): Promise<StorageResponse<WorkflowData[]>> {
        logger.log('📋 Listing workflows');

        return this.apiCall(`${this.apiUrl}?action=list&type=workflow`);
    }

    async deleteWorkflow(id: string): Promise<StorageResponse> {
        logger.log('🗑️ Deleting workflow:', id);

        return this.apiCall(`${this.apiUrl}?action=delete&type=workflow&id=${id}`, {
            method: 'DELETE',
        });
    }

    async updateWorkflow(id: string, updates: any): Promise<StorageResponse> {
        logger.log('� Updating workflow:', id, 'with updates:', Object.keys(updates));

        return this.apiCall(`${this.apiUrl}?action=update&type=workflow&id=${id}`, {
            method: 'POST',
            body: JSON.stringify({ updates }),
        });
    }

    // Template operations
    async storeTemplate(templateData: TemplateData): Promise<StorageResponse> {
        logger.log('📦 Storing template:', templateData.functionName);

        return this.apiCall(`${this.apiUrl}?action=store&type=template`, {
            method: 'POST',
            body: JSON.stringify({ data: templateData }),
        });
    }

    async retrieveTemplate(id: string): Promise<StorageResponse<TemplateData>> {
        logger.log('📥 Retrieving template:', id);

        return this.apiCall(`${this.apiUrl}?action=retrieve&type=template&id=${id}`);
    }

    async listTemplates(): Promise<StorageResponse<{ id: string; timestamp: number }[]>> {
        logger.log('📋 Listing templates');

        return this.apiCall(`${this.apiUrl}?action=list&type=template`);
    }

    async deleteTemplate(id: string): Promise<StorageResponse> {
        logger.log('🗑️ Deleting template:', id);

        return this.apiCall(`${this.apiUrl}?action=delete&type=template&id=${id}`, {
            method: 'DELETE',
        });
    }

    // Salt operations
    async generateSalt(): Promise<StorageResponse<{ salt: string; metadata: any }>> {
        logger.log('🧂 Generating new salt');

        return this.apiCall(`${this.apiUrl}?action=generate-salt`);
    }

    // SECURITY: Get workflow encryption key from backend (encrypted with session key)
    async getWorkflowKey(sessionId: string): Promise<StorageResponse<{ encryptedKey: { encrypted: string; iv: string; authTag: string } }>> {
        logger.log('🔑 Requesting workflow encryption key from backend');

        return this.apiCall(`${this.apiUrl}?action=get-workflow-key`, {
            method: 'POST',
            body: JSON.stringify({ sessionId }),
        });
    }

    async storeSalt(saltData: SaltData): Promise<StorageResponse> {
        logger.log('📦 Storing salt');

        return this.apiCall(`${this.apiUrl}?action=store&type=salt`, {
            method: 'POST',
            body: JSON.stringify({ data: saltData }),
        });
    }

    async retrieveSalt(id: string): Promise<StorageResponse<SaltData>> {
        logger.log('📥 Retrieving salt:', id);

        return this.apiCall(`${this.apiUrl}?action=retrieve&type=salt&id=${id}`);
    }

    async listSalts(): Promise<StorageResponse<{ id: string; timestamp: number }[]>> {
        logger.log('📋 Listing salts');

        return this.apiCall(`${this.apiUrl}?action=list&type=salt`);
    }

    async deleteSalt(id: string): Promise<StorageResponse> {
        logger.log('🗑️ Deleting salt:', id);

        return this.apiCall(`${this.apiUrl}?action=delete&type=salt&id=${id}`, {
            method: 'DELETE',
        });
    }

    // Utility methods
    async migrateFromLocalStorage(): Promise<void> {
        logger.log('🔄 Migrating data from localStorage to backend');

        try {
            // Migrate custom workflows
            const customWorkflows = JSON.parse(localStorage.getItem('customWorkflows') || '[]');
            for (const workflow of customWorkflows) {
                const workflowData: WorkflowData = {
                    name: `Custom: ${workflow.functionName}`,
                    description: `Custom workflow created from ${workflow.contract}.${workflow.functionName}`,
                    category: 'utility',
                    difficulty: 'intermediate',
                    estimatedTime: 10,
                    tags: ['custom', workflow.contract],
                    created: workflow.timestamp,
                    functionData: workflow.functionData,
                    contractAddresses: [workflow.contractAddress],
                    templateData: workflow
                };

                await this.storeWorkflow(workflowData);
            }

            // Migrate transaction templates
            const transactionTemplates = JSON.parse(localStorage.getItem('transactionTemplates') || '[]');
            for (const template of transactionTemplates) {
                const templateData: TemplateData = {
                    contract: template.contract,
                    contractAddress: template.contractAddress || '',
                    functionName: template.functionName,
                    functionData: template.functionData || '',
                    inputs: template.inputs,
                    timestamp: Date.now(),
                    description: `Template for ${template.contract}.${template.functionName}`
                };

                await this.storeTemplate(templateData);
            }

            logger.log('✅ Migration completed successfully');
        } catch (error) {
            logger.error('❌ Migration failed:', error);
            throw error;
        }
    }

    // Cross-session state management
    async saveWorkflowState(workflowId: string, state: any): Promise<void> {
        const stateData = {
            workflowId,
            state,
            timestamp: Date.now(),
            sessionId: this.generateSessionId()
        };

        // Store workflow state as a special template
        await this.storeTemplate({
            id: `state_${workflowId}`,
            contract: 'workflow_state',
            contractAddress: '',
            functionName: 'saveState',
            functionData: JSON.stringify(stateData),
            inputs: [],
            timestamp: Date.now(),
            description: `Workflow state for ${workflowId}`
        });
    }

    async loadWorkflowState(workflowId: string): Promise<any> {
        try {
            const response = await this.retrieveTemplate(`state_${workflowId}`);
            if (response.success && response.data) {
                const stateData = JSON.parse(response.data.functionData);
                return stateData.state;
            }
        } catch (error) {
            logger.log('No saved state found for workflow:', workflowId);
        }
        return null;
    }

    // Helper methods
    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Health check
    async healthCheck(): Promise<boolean> {
        try {
            await this.generateSalt();
            return true;
        } catch (error) {
            logger.error('Workflow storage service health check failed:', error);
            return false;
        }
    }

    // Fallback to localStorage if backend is unavailable
    async storeWorkflowFallback(workflowData: WorkflowData): Promise<void> {
        logger.log('📦 Storing workflow to localStorage (fallback)');

        const workflows = JSON.parse(localStorage.getItem('workflows_backup') || '[]');
        workflows.push({
            ...workflowData,
            id: workflowData.id || `workflow_${Date.now()}`,
            timestamp: Date.now()
        });
        localStorage.setItem('workflows_backup', JSON.stringify(workflows));
    }

    async listWorkflowsFallback(): Promise<WorkflowData[]> {
        logger.log('📋 Listing workflows from localStorage (fallback)');

        return JSON.parse(localStorage.getItem('workflows_backup') || '[]');
    }
}

// Export singleton instance
export const workflowStorageService = new WorkflowStorageService();
export default workflowStorageService;
